import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { FaCamera, FaUpload } from "react-icons/fa";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import {
  fetchAssignedPlots,
  fetchEnums,
  uploadMaintenancePhoto,
  createMaintenanceReport,
  fetchMaintenanceReports,
  fetchStaff,
} from "../api/api";

const CHECKLIST_ITEMS = [
  { key: "clearing_approach_road", label: "Clearing Approach Road" },
  { key: "deweeding_plot_cleaning", label: "Deweeding and Plot Cleaning" },
  { key: "basin_cleaning_preparation", label: "Basin Cleaning and Preparation" },
  { key: "checking_drip_lines", label: "Checking/Replacing/Re-fixing Drip Lines" },
  { key: "pruning_plants_trees", label: "Pruning Plants/Trees" },
];

const MANDATORY_PHOTO_SLOTS = [
  { slot: "main", label: "Plot Main Photo" },
  { slot: "plant", label: "Plot Plant Photo" },
  { slot: "basin", label: "Plot Basin Photo (Jeevamrutha)" },
];

const OPTIONAL_PHOTO_SLOTS = [
  { slot: "photo4", label: "4th Photo" },
  { slot: "photo5", label: "5th Photo" },
  { slot: "photo6", label: "6th Photo" },
  { slot: "photo7", label: "7th Photo" },
  { slot: "photo8", label: "8th Photo" },
  { slot: "photo9", label: "9th Photo" },
];

const FALLBACK_YIELD_TIMING = [
  "Yield is not ready yet",
  "The yield may be collected now",
  "The yield may be collected in 3-4 weeks",
  "The yield should be ready in 2-3 months",
];

const FALLBACK_YIELD_TYPES = [
  "Banana", "Coconut", "Drumstick", "Gooseberry", "Guava",
  "Jack Fruit", "Lemon", "Mango", "Papaya", "Sapota", "Others",
];

const EMPTY_CHECKLIST = CHECKLIST_ITEMS.reduce((acc, item) => ({ ...acc, [item.key]: "" }), {});

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDateTime(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}-${MONTHS[date.getMonth()]}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function RequiredLabel({ children }) {
  return (
    <span>
      {children} <span style={{ color: "red" }}>*</span>
    </span>
  );
}

function PhotoCaptureButtons({ onSelect }) {
  return (
    <>
      <label className="photo-icon-btn" title="Take Photo">
        <FaCamera />
        <input
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          onChange={(e) => onSelect(e.target.files[0])}
        />
      </label>

      <label className="photo-icon-btn" title="Upload from device">
        <FaUpload />
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => onSelect(e.target.files[0])}
        />
      </label>
    </>
  );
}

function Maintenance() {

  const { user, role } = useAuth();

  const [view, setView] = useState("list"); // list | form | preview
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const detailsRef = useRef(null);

  const isAdmin = role === "admin";
  const [supervisors, setSupervisors] = useState([]);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [supervisorFilter, setSupervisorFilter] = useState("");
  const [syncFilter, setSyncFilter] = useState(""); // "" | "synced" | "unsynced"
  const [pendingOnly, setPendingOnly] = useState(false);

  const [plots, setPlots] = useState([]);
  const [plotsError, setPlotsError] = useState("");

  const [yieldTimingOptions, setYieldTimingOptions] = useState(FALLBACK_YIELD_TIMING);
  const [yieldTypeOptions, setYieldTypeOptions] = useState(FALLBACK_YIELD_TYPES);

  const [plotId, setPlotId] = useState("");
  const [checklist, setChecklist] = useState(EMPTY_CHECKLIST);
  const [photos, setPhotos] = useState({}); // slot -> { fileName, url, uploading, error }
  const [visibleOptionalCount, setVisibleOptionalCount] = useState(0); // how many of OPTIONAL_PHOTO_SLOTS are shown
  const [yieldTiming, setYieldTiming] = useState("");
  const [yieldTypes, setYieldTypes] = useState([]);
  const [yieldRemarks, setYieldRemarks] = useState("");
  const [expenses, setExpenses] = useState({ tractor_cost: "", labour_cost: "", other_costs: "", actual_healthy_plants_count: "" });

  const [validationError, setValidationError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");


  // =========================
  // LOAD LIST DATA
  // =========================

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    setReportsError("");
    try {
      const data = await fetchMaintenanceReports();
      setReports(data);
    } catch (err) {
      setReportsError("Could not load maintenance reports.");
    } finally {
      setReportsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchStaff()
      .then((staff) => setSupervisors(staff.filter((s) => s.role?.name === "supervisor")))
      .catch(() => {});
  }, [isAdmin]);

  const filteredReports = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reports.filter((r) => {
      if (q) {
        const haystack = `${r.plot_number} ${r.supervisor_name}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (dateFrom && r.reporting_datetime.slice(0, 10) < dateFrom) return false;
      if (dateTo && r.reporting_datetime.slice(0, 10) > dateTo) return false;
      if (supervisorFilter && String(r.supervisor_id) !== supervisorFilter) return false;
      if (syncFilter === "synced" && !r.zoho_synced) return false;
      if (syncFilter === "unsynced" && r.zoho_synced) return false;
      if (pendingOnly) {
        const hasPending = CHECKLIST_ITEMS.some((item) => r[item.key] === "Pending");
        if (!hasPending) return false;
      }
      return true;
    });
  }, [reports, search, dateFrom, dateTo, supervisorFilter, syncFilter, pendingOnly]);

  useEffect(() => {
    if (selectedReport && detailsRef.current) {
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedReport]);

  useEffect(() => {
    fetchEnums()
      .then((enums) => {
        if (enums.yield_collection_timing) setYieldTimingOptions(enums.yield_collection_timing);
        if (enums.yield_type) setYieldTypeOptions(enums.yield_type);
      })
      .catch(() => {});
  }, []);


  // =========================
  // START A NEW REPORT
  // =========================

  const startNewReport = async () => {
    setPlotsError("");
    setPlotId("");
    setChecklist(EMPTY_CHECKLIST);
    setPhotos({});
    setVisibleOptionalCount(0);
    setYieldTiming("");
    setYieldTypes([]);
    setYieldRemarks("");
    setExpenses({ tractor_cost: "", labour_cost: "", other_costs: "", actual_healthy_plants_count: "" });
    setValidationError("");
    setSubmitError("");
    setView("form");

    try {
      const data = await fetchAssignedPlots();
      setPlots(data);
    } catch (err) {
      setPlotsError("Could not load your assigned plots.");
    }
  };


  // =========================
  // PHOTOS
  // =========================

  const handlePhotoSelect = async (slot, file) => {
    if (!file || !plotId) return;

    setPhotos((prev) => ({ ...prev, [slot]: { fileName: file.name, uploading: true, url: null, error: null } }));
    try {
      const data = await uploadMaintenancePhoto(plotId, slot, file);
      setPhotos((prev) => ({ ...prev, [slot]: { fileName: file.name, uploading: false, url: data.file_url, error: null } }));
    } catch (err) {
      setPhotos((prev) => ({ ...prev, [slot]: { fileName: file.name, uploading: false, url: null, error: "Upload failed" } }));
    }
  };

  const addAdditionalPhotoSlot = () => {
    setVisibleOptionalCount((prev) => Math.min(prev + 1, OPTIONAL_PHOTO_SLOTS.length));
  };

  const toggleYieldType = (type) => {
    setYieldTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };


  // =========================
  // VALIDATE + PREVIEW
  // =========================

  const goToPreview = (e) => {
    e.preventDefault();
    setValidationError("");

    if (!plotId) return setValidationError("Please select a plot.");
    if (CHECKLIST_ITEMS.some((item) => !checklist[item.key])) {
      return setValidationError("Please answer every checklist item.");
    }
    if (MANDATORY_PHOTO_SLOTS.some(({ slot }) => !photos[slot]?.url)) {
      return setValidationError("Please attach all 3 mandatory photos (Main, Plant, Basin).");
    }
    if (!yieldTiming) return setValidationError("Please select when the yield can be collected.");
    if (
      expenses.tractor_cost === "" || expenses.labour_cost === "" ||
      expenses.other_costs === "" || expenses.actual_healthy_plants_count === ""
    ) {
      return setValidationError("Please fill in all Maintenance Expenses fields.");
    }

    setView("preview");
  };


  // =========================
  // SUBMIT
  // =========================

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");

    const photoList = Object.entries(photos)
      .filter(([, p]) => p?.url)
      .map(([slot, p]) => ({ slot, file_url: p.url }));

    try {
      await createMaintenanceReport({
        plot_id: Number(plotId),
        clearing_approach_road: checklist.clearing_approach_road,
        deweeding_plot_cleaning: checklist.deweeding_plot_cleaning,
        basin_cleaning_preparation: checklist.basin_cleaning_preparation,
        checking_drip_lines: checklist.checking_drip_lines,
        pruning_plants_trees: checklist.pruning_plants_trees,
        when_to_collect_yield: yieldTiming,
        yield_types: yieldTiming === FALLBACK_YIELD_TIMING[0] ? [] : yieldTypes,
        yield_remarks: yieldRemarks || null,
        tractor_cost: Number(expenses.tractor_cost),
        labour_cost: Number(expenses.labour_cost),
        other_costs: Number(expenses.other_costs),
        actual_healthy_plants_count: Number(expenses.actual_healthy_plants_count),
        photos: photoList,
      });

      setView("list");
      await loadReports();
    } catch (err) {
      setSubmitError(err.response?.data?.detail || "Something went wrong submitting the report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPlot = plots.find((p) => String(p.id) === String(plotId));
  const showYieldTypes = yieldTiming && yieldTiming !== FALLBACK_YIELD_TIMING[0];


  // =========================
  // RENDER: LIST VIEW
  // =========================

  if (view === "list") {
    return (
      <div>
        <Navbar />
        <Sidebar />
        <div className="main-content">

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h1>Maintenance Reports</h1>
            <button className="login-btn" style={{ width: "auto", padding: "10px 20px" }} onClick={startNewReport}>
              + New Report
            </button>
          </div>

          {reportsError && <p className="login-error" style={{ color: "red" }}>{reportsError}</p>}

          <div className="filter-row" style={{ display: "flex", gap: "10px", margin: "15px 0", flexWrap: "wrap" }}>

            <input
              type="text"
              placeholder="Search plot number or supervisor..."
              className="search-bar"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: "220px" }}
            />

            <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#eef2ff", padding: "4px 10px", borderRadius: "8px" }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#3730a3", whiteSpace: "nowrap" }}>
                Reported between:
              </span>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="From date" />
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="To date" />
            </div>

            {isAdmin && (
              <select value={supervisorFilter} onChange={(e) => setSupervisorFilter(e.target.value)}>
                <option value="">All Supervisors</option>
                {supervisors.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
            )}

            <select value={syncFilter} onChange={(e) => setSyncFilter(e.target.value)}>
              <option value="">All (Zoho sync)</option>
              <option value="synced">Synced to Zoho</option>
              <option value="unsynced">Not yet synced</option>
            </select>

            <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />
              Has pending checklist items
            </label>

            {(search || dateFrom || dateTo || supervisorFilter || syncFilter || pendingOnly) && (
              <button
                type="button"
                className="register-btn"
                style={{ width: "auto", padding: "4px 12px" }}
                onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setSupervisorFilter(""); setSyncFilter(""); setPendingOnly(false); }}
              >
                Clear filters
              </button>
            )}

          </div>

          {reportsLoading && <p>Loading…</p>}

          {!reportsLoading && (
            <p style={{ color: "#666" }}>
              Showing {filteredReports.length} of {reports.length} report(s)
            </p>
          )}

          <div className="dashboard-grid">
            {!reportsLoading && filteredReports.length === 0 && <p>No maintenance reports match these filters.</p>}

            {filteredReports.map((report) => (
              <div className="card" key={report.id}>
                <h3>Plot #{report.plot_number}</h3>
                <p>👷 {report.supervisor_name}</p>
                <p>🗓 {new Date(report.reporting_datetime).toLocaleString()}</p>
                <p>🚜 Tractor ₹{report.tractor_cost} · 👷 Labour ₹{report.labour_cost} · 🧾 Other ₹{report.other_costs}</p>
                <p>🌱 Healthy plants: {report.actual_healthy_plants_count}</p>
                <p>📸 {(report.photos || []).length} photo(s)</p>
                <p>{report.zoho_synced ? "✅ Synced to Zoho" : "⚠️ Not yet synced to Zoho"}</p>
                <button
                  className="login-btn"
                  style={{ width: "auto", padding: "8px 16px" }}
                  onClick={() => setSelectedReport(report)}
                >
                  View Report
                </button>
              </div>
            ))}
          </div>

          {selectedReport && (
            <div className="section-card" style={{ marginTop: "20px" }} ref={detailsRef}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3>Report Details — Plot #{selectedReport.plot_number}</h3>
                <button
                  className="register-btn"
                  style={{ width: "auto", padding: "6px 14px" }}
                  onClick={() => setSelectedReport(null)}
                >
                  Close
                </button>
              </div>

              <p><strong>Supervisor:</strong> {selectedReport.supervisor_name}</p>
              <p><strong>Reporting Date/Time:</strong> {new Date(selectedReport.reporting_datetime).toLocaleString()}</p>
              <p><strong>Location:</strong> {selectedReport.latitude != null && selectedReport.longitude != null
                ? (
                  <a
                    href={`https://maps.google.com/?q=${selectedReport.latitude},${selectedReport.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selectedReport.latitude}, {selectedReport.longitude}
                  </a>
                )
                : "—"}
              </p>
              <p>
                <strong>Zoho Sync:</strong>{" "}
                {selectedReport.zoho_synced ? "✅ Synced" : "⚠️ Not synced"}
                {selectedReport.zoho_id ? ` (ID: ${selectedReport.zoho_id})` : ""}
                {selectedReport.zoho_sync_error ? ` — ${selectedReport.zoho_sync_error}` : ""}
              </p>

              <h4>Plot Maintenance Checklist</h4>
              {CHECKLIST_ITEMS.map((item) => (
                <p key={item.key}>{item.label}: <strong>{selectedReport[item.key] || "—"}</strong></p>
              ))}

              <h4>Yield Report</h4>
              <p><strong>When to collect:</strong> {selectedReport.when_to_collect_yield || "—"}</p>
              {selectedReport.yield_types?.length > 0 && (
                <p><strong>Yield types:</strong> {selectedReport.yield_types.join(", ")}</p>
              )}
              <p><strong>Remarks:</strong> {selectedReport.yield_remarks || "—"}</p>

              <h4>Maintenance Expenses</h4>
              <p>Tractor Cost: ₹{selectedReport.tractor_cost}</p>
              <p>Labour Cost: ₹{selectedReport.labour_cost}</p>
              <p>Other Costs: ₹{selectedReport.other_costs}</p>
              <p>Actual Number of Healthy Plants/Trees: {selectedReport.actual_healthy_plants_count}</p>

              <h4>Photos</h4>
              {(selectedReport.photos || []).length === 0 && <p>No photos attached.</p>}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {(selectedReport.photos || []).map((photo) => (
                  <a href={photo.file_url} target="_blank" rel="noopener noreferrer" key={photo.id || photo.file_url}>
                    <img
                      src={photo.file_url}
                      alt={photo.slot}
                      style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "6px" }}
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }


  // =========================
  // RENDER: PREVIEW
  // =========================

  if (view === "preview") {
    return (
      <div>
        <Navbar />
        <Sidebar />
        <div className="main-content">

          <h1>Preview Maintenance Report</h1>

          {submitError && <p className="login-error" style={{ color: "red" }}>{submitError}</p>}

          <div className="section-card">
            <h3>Details</h3>
            <p><strong>Plot Number:</strong> {selectedPlot?.plot_number}</p>
            <p><strong>Supervisor:</strong> {user?.full_name}</p>
            <p><strong>Reporting Date/Time:</strong> {formatDateTime(new Date())}</p>
          </div>

          <div className="section-card">
            <h3>Plot Maintenance Checklist</h3>
            {CHECKLIST_ITEMS.map((item) => (
              <p key={item.key}>{item.label}: <strong>{checklist[item.key]}</strong></p>
            ))}
          </div>

          <div className="section-card">
            <h3>Plot Photos</h3>
            {[...MANDATORY_PHOTO_SLOTS, ...OPTIONAL_PHOTO_SLOTS.filter(({ slot }) => photos[slot]?.url)].map(({ slot, label }) => (
              <p key={slot}>{label}: {photos[slot]?.url ? photos[slot].fileName : "—"}</p>
            ))}
          </div>

          <div className="section-card">
            <h3>Yield Report</h3>
            <p><strong>When to collect:</strong> {yieldTiming}</p>
            {showYieldTypes && <p><strong>Yield types:</strong> {yieldTypes.join(", ") || "—"}</p>}
            <p><strong>Remarks:</strong> {yieldRemarks || "—"}</p>
          </div>

          <div className="section-card">
            <h3>Maintenance Expenses</h3>
            <p>Tractor Cost: ₹{expenses.tractor_cost}</p>
            <p>Labour Cost: ₹{expenses.labour_cost}</p>
            <p>Other Costs: ₹{expenses.other_costs}</p>
            <p>Actual Number of Healthy Plants/Trees: {expenses.actual_healthy_plants_count}</p>
          </div>

          <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
            <button className="login-btn" style={{ width: "auto", padding: "10px 20px" }} onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting…" : "Submit"}
            </button>
            <button className="register-btn" style={{ width: "auto", padding: "10px 20px" }} onClick={() => setView("form")} disabled={submitting}>
              Back to Edit
            </button>
          </div>

        </div>
      </div>
    );
  }


  // =========================
  // RENDER: FORM VIEW
  // =========================

  return (
    <div>
      <Navbar />
      <Sidebar />
      <div className="main-content">

        <h1>New Maintenance Report</h1>

        {plotsError && <p className="login-error" style={{ color: "red" }}>{plotsError}</p>}
        {validationError && <p className="login-error" style={{ color: "red" }}>{validationError}</p>}

        <form onSubmit={goToPreview}>

          <div className="section-card">
            <label><RequiredLabel>Plot Number</RequiredLabel></label>
            <select value={plotId} onChange={(e) => setPlotId(e.target.value)} required>
              <option value="">Select a plot</option>
              {plots.map((plot) => (
                <option key={plot.id} value={plot.id}>{plot.plot_number} — {plot.location}</option>
              ))}
            </select>

            <p><strong>Supervisor:</strong> {user?.full_name} ({role})</p>
            <p><strong>Reporting Date/Time:</strong> {formatDateTime(new Date())}</p>
          </div>

          <div className="section-card">
            <h3>Plot Maintenance Checklist</h3>
            {CHECKLIST_ITEMS.map((item) => (
              <div
                key={item.key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                  padding: "10px 0",
                  borderBottom: "1px solid #eee",
                }}
              >
                <label style={{ margin: 0 }}><RequiredLabel>{item.label}</RequiredLabel></label>
                <div style={{ display: "flex", gap: "20px", flex: "none" }}>
                  {["Done", "Pending"].map((option) => (
                    <label key={option} style={{ display: "flex", alignItems: "center", gap: "6px", margin: 0 }}>
                      <input
                        type="radio"
                        name={item.key}
                        value={option}
                        checked={checklist[item.key] === option}
                        onChange={() => setChecklist((prev) => ({ ...prev, [item.key]: option }))}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="section-card">
            <h3>Plot Photos</h3>

            {!plotId && <p>Select a plot above before attaching photos.</p>}

            {plotId && MANDATORY_PHOTO_SLOTS.map(({ slot, label }) => (
              <div key={slot} style={{ marginBottom: "12px" }}>
                <label><RequiredLabel>{label}</RequiredLabel></label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="text"
                    readOnly
                    value={photos[slot]?.fileName || ""}
                    placeholder="No photo selected"
                    style={{ flex: 1 }}
                  />
                  {photos[slot]?.uploading ? (
                    <span>Uploading…</span>
                  ) : (
                    <PhotoCaptureButtons onSelect={(file) => handlePhotoSelect(slot, file)} />
                  )}
                  {photos[slot]?.url && <span style={{ color: "#2e7d32" }}>✅</span>}
                  {photos[slot]?.error && <span style={{ color: "red" }}>{photos[slot].error}</span>}
                </div>
              </div>
            ))}

            {plotId && visibleOptionalCount > 0 && <hr style={{ margin: "15px 0" }} />}

            {plotId && OPTIONAL_PHOTO_SLOTS.slice(0, visibleOptionalCount).map(({ slot, label }) => (
              <div key={slot} style={{ marginBottom: "12px" }}>
                <label>{label}</label>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="text"
                    readOnly
                    value={photos[slot]?.fileName || ""}
                    placeholder="No photo selected"
                    style={{ flex: 1 }}
                  />
                  {photos[slot]?.uploading ? (
                    <span>Uploading…</span>
                  ) : (
                    <PhotoCaptureButtons onSelect={(file) => handlePhotoSelect(slot, file)} />
                  )}
                  {photos[slot]?.url && <span style={{ color: "#2e7d32" }}>✅</span>}
                </div>
              </div>
            ))}

            {plotId && visibleOptionalCount < OPTIONAL_PHOTO_SLOTS.length && (
              <button type="button" className="register-btn" style={{ width: "auto", padding: "8px 16px" }} onClick={addAdditionalPhotoSlot}>
                + Add Additional Photo
              </button>
            )}
          </div>

          <div className="section-card">
            <h3>Yield Report</h3>

            <label><RequiredLabel>When to Collect the Yield</RequiredLabel></label>
            <div>
              {yieldTimingOptions.map((option) => (
                <label key={option} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                  <input
                    type="radio"
                    name="yield_timing"
                    value={option}
                    checked={yieldTiming === option}
                    onChange={() => { setYieldTiming(option); setYieldTypes([]); }}
                  />
                  {option}
                </label>
              ))}
            </div>

            {showYieldTypes && (
              <div style={{ margin: "10px 0" }}>
                <label>Select fruits/vegetables</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                  {yieldTypeOptions.map((type) => (
                    <label key={type} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <input
                        type="checkbox"
                        checked={yieldTypes.includes(type)}
                        onChange={() => toggleYieldType(type)}
                      />
                      {type}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <label>Yield Remarks</label>
            <textarea
              value={yieldRemarks}
              onChange={(e) => setYieldRemarks(e.target.value)}
              rows={3}
              style={{ width: "100%" }}
            />
          </div>

          <div className="section-card">
            <h3>Maintenance Expenses</h3>

            <label><RequiredLabel>Tractor Cost</RequiredLabel></label>
            <input
              type="number"
              value={expenses.tractor_cost}
              onChange={(e) => setExpenses((prev) => ({ ...prev, tractor_cost: e.target.value }))}
              required
            />

            <label><RequiredLabel>Labour Cost</RequiredLabel></label>
            <input
              type="number"
              value={expenses.labour_cost}
              onChange={(e) => setExpenses((prev) => ({ ...prev, labour_cost: e.target.value }))}
              required
            />

            <label><RequiredLabel>Other Costs</RequiredLabel></label>
            <input
              type="number"
              value={expenses.other_costs}
              onChange={(e) => setExpenses((prev) => ({ ...prev, other_costs: e.target.value }))}
              required
            />

            <label><RequiredLabel>Actual Number of Healthy Plants/Trees</RequiredLabel></label>
            <input
              type="number"
              value={expenses.actual_healthy_plants_count}
              onChange={(e) => setExpenses((prev) => ({ ...prev, actual_healthy_plants_count: e.target.value }))}
              required
            />
          </div>

          <div style={{ display: "flex", gap: "10px", margin: "15px 0" }}>
            <button type="submit" className="login-btn" style={{ width: "auto", padding: "10px 20px" }}>
              Preview
            </button>
            <button
              type="button"
              className="register-btn"
              style={{ width: "auto", padding: "10px 20px" }}
              onClick={() => setView("list")}
            >
              Cancel
            </button>
          </div>

        </form>

      </div>
    </div>
  );
}

export default Maintenance;
